import { RegistryType, RegistryHistoryType } from '@/api/backend/types';
import { RegistryStatusBadge } from '@/components/registry-status-badge';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { formatIsoDate } from '@/lib/utils';
import {
  DataTableColumnType,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@opaz/ui-components';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

export const ServiceProviderHistoryTab = ({ data }: { data: RegistryType & { history?: RegistryHistoryType[] } }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const rows = data.history ?? [];
  const columns: DataTableColumnType<RegistryHistoryType>[] = [
    {
      title: t('registry.serviceProvider.sectionTabs.historyTab.table.headings.applicationNumber'),
      render: (item) => item.applicationNumber,
    },
    {
      title: t('registry.serviceProvider.sectionTabs.historyTab.table.headings.date'),
      render: (item) => formatIsoDate(item.date, i18n.language),
    },
    {
      title: t('registry.serviceProvider.sectionTabs.historyTab.table.headings.type'),
      render: (item) => t(`backend.codes.${item.applicationTypeCode}` as unknown as TemplateStringsArray),
    },
    {
      title: t('registry.serviceProvider.sectionTabs.historyTab.table.headings.status'),
      render: (item) => <RegistryStatusBadge statusCode={item.statusCode} />,
    },
    {
      title: t('registry.serviceProvider.sectionTabs.historyTab.table.viewDetails'),
      render: (item) => (
        <Link
          onClick={() => {
            navigate({
              to: '/registry/service-providers/$providerId',
              params: { providerId: data.registryNumber },
              search: { version: item.applicationNumber },
            });

            window.scrollTo(0, 0);
          }}
        >
          {t('registry.serviceProvider.sectionTabs.historyTab.table.viewDetails')}
        </Link>
      ),
    },
  ];
  return (
    <>
      <h2 className="text-foreground text-xl font-bold mb-4">
        {t('registry.serviceProvider.sectionTabs.historyTab.table.title')}
      </h2>

      <ResponsiveDataView
        columns={columns}
        data={rows}
        emptyMessage={t('registry.serviceProvider.sectionTabs.historyTab.table.emptyState')}
        getRowKey={(item, index) => item.applicationNumber ?? index}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                {t('registry.serviceProvider.sectionTabs.historyTab.table.headings.applicationNumber')}
              </TableHead>
              <TableHead>{t('registry.serviceProvider.sectionTabs.historyTab.table.headings.date')}</TableHead>
              <TableHead>{t('registry.serviceProvider.sectionTabs.historyTab.table.headings.type')}</TableHead>
              <TableHead>{t('registry.serviceProvider.sectionTabs.historyTab.table.headings.status')}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.history && data.history.length > 0 ? (
              data.history.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.applicationNumber}</TableCell>
                  <TableCell>{formatIsoDate(item.date, i18n.language)}</TableCell>
                  <TableCell>
                    {t(`backend.codes.${item.applicationTypeCode}` as unknown as TemplateStringsArray)}
                  </TableCell>
                  <TableCell>
                    <RegistryStatusBadge statusCode={item.statusCode} />
                  </TableCell>
                  <TableCell>
                    <Link
                      onClick={() => {
                        navigate({
                          to: '/registry/service-providers/$providerId',
                          params: { providerId: data.registryNumber },
                          search: { version: item.applicationNumber },
                        });

                        window.scrollTo(0, 0);
                      }}
                    >
                      {t('registry.serviceProvider.sectionTabs.historyTab.table.viewDetails')}
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  {t('registry.serviceProvider.sectionTabs.historyTab.table.emptyState')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ResponsiveDataView>
    </>
  );
};
